import { createFileRoute, Link } from '@tanstack/react-router'
import { ArrowRight } from 'lucide-react'
import { StartFreeButton } from '@/components/marketing/free-plan-cta'
import { Button } from '@/components/ui/button'
import { MONITOR_LOCATIONS } from '@/lib/check-locations'

const SLUG = 'how-to-check-if-a-website-is-down-from-multiple-locations'
const URL = `https://uptrack.app/blog/${SLUG}`
const TITLE = 'Check if a Website Is Down From Multiple Locations — Uptrack'
const HEADLINE = 'How to check if a website is down from multiple locations'
const DESCRIPTION =
  'How to check if a website is down from multiple locations: why one location misleads, how to read each result, and what to do next. Free, no signup.'
const PUBLISHED = '2026-10-12'

const FAQ: { q: string; a: string }[] = [
  {
    q: 'How do I check if a website is down for everyone or just me?',
    a: 'Request the site from several places at once. If every location fails, the site is down for everyone. If only your location fails, the problem is on your side: your network, your DNS resolver or a local block. The free Uptrack checker requests the site from 24 locations on six continents and shows the status code and response time for each one.',
  },
  {
    q: 'Why does a site load for me but show as down in another country?',
    a: 'The request may reach a different server. CDNs answer from the nearest edge, DNS can return different addresses by region, and some sites block whole countries. A failure at one edge, a routing problem between two networks, or a country block all look like "down" from one place and "up" from another.',
  },
  {
    q: 'How many locations do I need to tell a local problem from a global one?',
    a: 'Three locations on different continents are enough to separate a local problem from a global one. More locations help you see regional patterns, for example one continent failing while the rest are fine. A single location can never tell the two apart.',
  },
  {
    q: 'Is the multi-location checker free?',
    a: 'Yes. The free checker needs no signup. Checks are spaced a few seconds apart for each visitor, so one person cannot overload a site.',
  },
  {
    q: 'Does the free checker alert me when a site goes down?',
    a: 'No. It is a one-time check. To be told when a site goes down you need monitoring, which checks on a schedule and sends an alert to email, Slack, Discord or Telegram.',
  },
]

export const Route = createFileRoute('/_site/blog/how-to-check-if-a-website-is-down-from-multiple-locations')({
  component: HowToCheckPage,
  head: () => ({
    meta: [
      { title: TITLE },
      { name: 'description', content: DESCRIPTION },
      { property: 'og:title', content: TITLE },
      { property: 'og:description', content: DESCRIPTION },
      { property: 'og:image', content: `https://uptrack.app/og/blog-${SLUG}.png` },
      { property: 'og:type', content: 'article' },
      { property: 'og:url', content: URL },
      { property: 'article:published_time', content: PUBLISHED },
    ],
    links: [{ rel: 'canonical', href: URL }],
    scripts: [
      {
        type: 'application/ld+json',
        children: JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'BlogPosting',
          headline: HEADLINE,
          description: DESCRIPTION,
          datePublished: PUBLISHED,
          dateModified: PUBLISHED,
          author: { '@type': 'Organization', name: 'Uptrack' },
          publisher: { '@type': 'Organization', name: 'Uptrack', url: 'https://uptrack.app' },
          mainEntityOfPage: URL,
        }),
      },
      {
        type: 'application/ld+json',
        children: JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'FAQPage',
          mainEntity: FAQ.map((f) => ({
            '@type': 'Question',
            name: f.q,
            acceptedAnswer: { '@type': 'Answer', text: f.a },
          })),
        }),
      },
    ],
  }),
})

const causes = [
  {
    title: 'A CDN edge fails',
    body: 'A CDN serves your site from many edge servers. When one edge breaks, only the visitors routed to it see errors. Your origin server is fine, and a check from your office, which uses a healthy edge, says everything is up.',
  },
  {
    title: 'A regional outage',
    body: 'A cloud region, a data center or a network provider can fail in one part of the world. Your site stays reachable from everywhere else. One location cannot tell you this is happening, because it only sees its own part of the network.',
  },
  {
    title: 'DNS has not caught up',
    body: 'After you change a DNS record, resolvers keep the old answer until its TTL runs out. Some visitors reach the new server and some still reach the old one. The same domain works in one country and fails in another for hours.',
  },
  {
    title: 'The site blocks a country or a network',
    body: 'Firewalls, bot protection and legal rules can refuse requests from certain countries or from data-center addresses. The server is up and answering, but it answers 403 or 451 to some places on purpose.',
  },
  {
    title: 'Routing between two networks breaks',
    body: 'The path between a visitor\'s network and your server crosses several other networks. A bad route or a congested link can make your site slow or unreachable for one provider and fine for all others.',
  },
]

const patterns = [
  {
    pattern: 'Every location fails',
    cause: 'The site, its host or its DNS is down for everyone.',
    next: 'Check your host\'s status page, the last deploy, the TLS certificate and the DNS records. Fix the origin first.',
  },
  {
    pattern: 'One region fails, the rest are fine',
    cause: 'A regional issue: one CDN edge, one cloud region or one network route.',
    next: 'Check your CDN provider\'s status page. Look for a geo rule in your firewall. Ask the provider about that region. Your origin is probably healthy.',
  },
  {
    pattern: 'One region answers slowly',
    cause: 'Long or congested route to your server, or no edge nearby.',
    next: 'Compare the response times. If one region is several times slower, move content closer or add an edge for that region.',
  },
  {
    pattern: 'Some locations answer 403 or 451',
    cause: 'A deliberate block: a firewall rule, bot protection or a legal restriction.',
    next: 'Read the response body and headers. See the guide to HTTP 451 for the legal case. Decide if the block is intended.',
  },
  {
    pattern: 'Different locations show different pages',
    cause: 'DNS propagation, geo-based routing or a cache that has not refreshed.',
    next: 'Wait for the TTL to expire. Purge the CDN cache. Compare the DNS answers from several resolvers.',
  },
]

function HowToCheckPage() {
  return (
    <article className="article-prose container mx-auto max-w-2xl px-4 py-16">
      <p className="mb-3 text-sm text-muted-foreground">October 12, 2026</p>
      <h1 className="font-sans mb-6 text-4xl font-semibold">{HEADLINE}</h1>

      <section className="mb-12">
        <p className="mb-4 text-lg leading-[1.7] text-body">
          To check if a website is down from multiple locations, request the same URL from servers in different
          countries at the same time and compare the answers. If every location fails, the site is down for everyone.
          If only some fail, you are looking at a regional problem, and the site may be fine for most of your visitors.
        </p>
        <p className="mb-4 text-lg leading-[1.7] text-body">
          A single check gives you one answer from one place. That answer can be wrong in both directions: a site that
          loads for you can be broken for another continent, and a site that fails for you can be fine for everyone
          else. This guide shows why, how to read a result that comes back from many places, and what to do next.
        </p>
        <p className="text-lg leading-[1.7] text-body">
          You can follow along with the free{' '}
          <Link to="/tools/is-website-down" className="font-medium underline">
            website down checker
          </Link>
          . It needs no signup.
        </p>
      </section>

      <section className="mb-12">
        <h2 className="font-sans mb-4 text-2xl font-semibold">Why a single location can lie to you</h2>
        <p className="mb-6 text-lg leading-[1.7] text-body">
          Your site is not one thing in one place. Between a visitor and your server there is DNS, one or more CDN
          edges, several networks and often a firewall. Each part can fail for some visitors and not for others. These
          are the five causes you meet most often.
        </p>
        <div className="space-y-4">
          {causes.map((c) => (
            <div key={c.title} className="rounded-lg border border-border bg-card p-5">
              <h3 className="font-sans mb-2 font-semibold">{c.title}</h3>
              <p className="text-lg leading-[1.7] text-body">{c.body}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="mb-12">
        <h2 className="font-sans mb-4 text-2xl font-semibold">
          How to check if a website is down from multiple locations, step by step
        </h2>
        <ol className="mb-6 list-decimal space-y-4 pl-6 text-lg leading-[1.7] text-body">
          <li>
            Open the{' '}
            <Link to="/tools/is-website-down" className="font-medium underline">
              free website down checker
            </Link>{' '}
            and enter the address of the site, for example <code>example.com</code>.
          </li>
          <li>
            Start the check. Uptrack requests the site from 24 locations on six continents: Europe, North and South
            America, Asia, Australia and Africa. The results arrive one location at a time.
          </li>
          <li>
            Read the verdict first. It says if the site is up, down or only down in some places.
          </li>
          <li>
            Then read the list. Each row is one city with the HTTP status code and the response time in milliseconds.
            The checker treats any answer below 500 as a reply from a working server and shows you the code, so a 403
            or a 451 stays visible instead of being hidden.
          </li>
          <li>
            Run the check again after a minute if the result is mixed. A failure that shows up in the same locations
            both times is real. A failure that disappears was a short glitch.
          </li>
        </ol>
        <p className="text-lg leading-[1.7] text-body">
          Checks are spaced a few seconds apart for each visitor, so one person cannot overload a site. Do not use the
          tool to test the load a site can carry.
        </p>
      </section>

      <section className="mb-12">
        <h2 className="font-sans mb-4 text-2xl font-semibold">How to read a result from many places</h2>
        <p className="mb-6 text-lg leading-[1.7] text-body">
          The pattern across locations tells you more than any single row. Use this table to turn a pattern into a
          likely cause and a next step.
        </p>
        <div className="overflow-x-auto" tabIndex={0} role="region" aria-label="Scrollable table">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b">
                <th className="pb-3 pr-4 text-left font-medium text-muted-foreground">What you see</th>
                <th className="pb-3 px-3 text-left font-medium text-muted-foreground">Likely cause</th>
                <th className="pb-3 pl-3 text-left font-medium text-muted-foreground">What to do next</th>
              </tr>
            </thead>
            <tbody>
              {patterns.map((p) => (
                <tr key={p.pattern} className="border-b align-top last:border-0">
                  <td className="py-3 pr-4 font-medium">{p.pattern}</td>
                  <td className="py-3 px-3 text-body">{p.cause}</td>
                  <td className="py-3 pl-3 text-body">{p.next}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="mt-4 text-lg leading-[1.7] text-body">
          If the odd rows show 403 or 451, the server is up and refusing on purpose. Our guide to{' '}
          <Link to="/blog/http-451-unavailable-for-legal-reasons" className="font-medium underline">
            HTTP 451 Unavailable For Legal Reasons
          </Link>{' '}
          explains the legal case.
        </p>
      </section>

      <section className="mb-12">
        <h2 className="font-sans mb-4 text-2xl font-semibold">Partial outages after a DNS change</h2>
        <p className="mb-4 text-lg leading-[1.7] text-body">
          If you changed a DNS record in the last day, a mixed result is normal. Resolvers around the world keep the old
          answer until its TTL (time to live) expires, so some visitors still reach the old server. Our guide to{' '}
          <Link to="/glossary/dns-propagation" className="font-medium underline">
            DNS propagation
          </Link>{' '}
          explains how long this takes and how a lower TTL before a migration shortens it.
        </p>
        <p className="text-lg leading-[1.7] text-body">
          The test is simple. Check the site from many locations and compare. If the failing locations are the same on
          every run and all of them show an old address or an old page, wait. If they stay down after the TTL has
          passed, the new record is wrong.
        </p>
      </section>

      <section className="mb-12">
        <h2 className="font-sans mb-4 text-2xl font-semibold">From a one-off check to monitoring</h2>
        <p className="mb-4 text-lg leading-[1.7] text-body">
          A check tells you what is true now. It does not tell you what happened at 3 a.m., and it does not wake you up.
          For that you need monitoring: the same request, repeated every 30 seconds, from several places, with an alert
          when it fails. {MONITOR_LOCATIONS.sentence} {MONITOR_LOCATIONS.confirm} The free plan checks from Nuremberg
          only. Paid plans add more locations.
        </p>
        <p className="mb-4 text-lg leading-[1.7] text-body">
          The confirmation step matters. One failed check from one place is often a glitch. Asking several locations to
          agree stops most false alarms. Read{' '}
          <Link to="/blog/multi-region-consensus" className="font-medium underline">
            how multi-region consensus works
          </Link>{' '}
          and{' '}
          <Link to="/blog/why-alert-confirmation" className="font-medium underline">
            why alert confirmation matters
          </Link>{' '}
          for the details.
        </p>
        <p className="text-lg leading-[1.7] text-body">
          Faster detection also shortens the time to recover. The{' '}
          <Link to="/glossary/mttr" className="font-medium underline">
            MTTR
          </Link>{' '}
          glossary page explains why detection is the part you can improve first.
        </p>
      </section>

      <section className="mb-12">
        <h2 className="font-sans mb-4 text-2xl font-semibold">When this is not the right tool</h2>
        <ul className="list-disc space-y-3 pl-6 text-lg leading-[1.7] text-body">
          <li>
            <strong>You need to know it before your customers do.</strong> A one-time check cannot do that. Use
            monitoring with alerts.
          </li>
          <li>
            <strong>The page is behind a login.</strong> The checker requests the public address. It cannot sign in. A
            browser check that runs your login flow is the right tool for that.
          </li>
          <li>
            <strong>The problem comes and goes.</strong> A problem that lasts 20 seconds is easy to miss in a manual
            check. Repeated monitoring catches it.
          </li>
          <li>
            <strong>The server is on a private network.</strong> The checker reaches only public addresses. It cannot
            see an internal service.
          </li>
          <li>
            <strong>You want to know how many users are hit.</strong> The checker tests from data centers, not from your
            visitors' own networks. It shows where a problem exists, not how many people feel it.
          </li>
        </ul>
      </section>

      <section className="mb-12">
        <h2 className="font-sans mb-4 text-2xl font-semibold">FAQ</h2>
        <div className="space-y-6">
          {FAQ.map((f) => (
            <div key={f.q}>
              <h3 className="font-sans mb-1 font-semibold">{f.q}</h3>
              <p className="text-lg leading-[1.7] text-body">{f.a}</p>
            </div>
          ))}
        </div>
      </section>

      <div className="rounded-lg border border-border bg-muted p-8 text-center">
        <h2 className="font-sans mb-3 text-xl font-semibold">Stop checking by hand</h2>
        <p className="mb-6 text-lg leading-[1.7] text-body">
          Monitor your site every 30 seconds and get an alert when it fails. Free for 5 monitors. Paid plans from $3/mo
          with a 30-day money-back guarantee.
        </p>
        <div className="flex flex-wrap items-center justify-center gap-3">
          <StartFreeButton />
          <Button size="lg" variant="outline" asChild>
            <Link to="/pricing">
              See plans
              <ArrowRight className="ml-2 h-4 w-4" />
            </Link>
          </Button>
        </div>
      </div>
    </article>
  )
}
